import { TaskResponse, TasksService, TaskStatus } from "client/openapi";
import TaskViewDialog from "components/Dashboard/PeoplePanel/TaskViewDialog";
import { errorText } from "components/Forms/formsApi";
import { formatDateTime } from "components/Forms/ResponseDialog";
import { useState } from "react";
import Notifications from "util/notifications";
import { isPast } from "util/time";
import { ActionPill, actionLabel, StatusPill, statusOf } from "./ActivityPill";
import {
  ActivityTable,
  AutomationPill,
  CELL,
  DetailsPopover,
  PeoplePills,
  PreviewButton,
  PreviewLine,
  PreviewParts,
  useAutomationIndex,
} from "./ActivityTabParts";
import { useReference } from "./hooks";
import type { ActivityRow } from "./types";
import { reasonWords } from "./words";

/**
 * What a row did, in its pop-up: why it failed or was skipped, or what
 * else it says once done; then its action's part (PreviewParts: a
 * message's To, By and wording as sent, a task's people and due date, a
 * move's From and To, each field before and after), and a task's Open task.
 */
function ActionDetails({
  row,
  onOpenTasks,
}: {
  row: ActivityRow;
  onOpenTasks: (taskIds: string[]) => void;
}) {
  const ref = useReference();
  const { details } = row;
  const line =
    statusOf(row) === "completed" ? details.note : reasonWords(ref, row);
  let taskIds: string[] = [];
  if (row.action === "task") {
    taskIds = details.tasks.flatMap((task) =>
      task.name !== null && task.id !== null ? [task.id] : []
    );
    if (!details.tasks.length && row.taskId) taskIds = [row.taskId];
  }
  // The tasks a Close a task step closed, still there.
  if (row.action === "close_task")
    taskIds = (details.close?.tasks ?? []).flatMap((task) =>
      task.name !== null && task.id !== null ? [task.id] : []
    );
  return (
    <div className="space-y-3 p-3 text-sm">
      <div>
        <p className="break-words font-semibold text-gray-900">{row.title}</p>
        {line && (
          <p
            className={`mt-0.5 break-words ${
              row.pill === "failed" ? "text-red-600" : "text-gray-600"
            }`}
          >
            {line}
          </p>
        )}
      </div>
      <PreviewParts action={row.action} details={details} title={row.title} />
      {taskIds.length > 0 && (
        <button
          type="button"
          className="font-semibold text-green-700 hover:underline"
          onClick={() => onOpenTasks(taskIds)}
        >
          {taskIds.length > 1 ? "Open tasks" : "Open task"}
        </button>
      )}
    </div>
  );
}

/**
 * One row of Done: its Status; its Action and its Preview, each opening
 * what it did; who it was about; its automation; when.
 */
function DoneRow({
  row,
  automations,
  onOpenTasks,
}: {
  row: ActivityRow;
  automations: ReturnType<typeof useAutomationIndex>;
  onOpenTasks: (taskIds: string[]) => void;
}) {
  const label = actionLabel(row);
  // Wider for a message's wording.
  const wide = !!row.details.message?.sent.length;
  const details = (close: () => void) => (
    <ActionDetails
      row={row}
      onOpenTasks={(taskIds) => {
        close();
        onOpenTasks(taskIds);
      }}
    />
  );
  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className={CELL}>
        <StatusPill row={row} />
      </td>
      <td className={CELL}>
        <DetailsPopover
          label={row.title}
          wide={wide}
          trigger={
            <ActionPill
              action={row.action}
              label={label}
              aria-label={`${label}: ${row.title}`}
            />
          }
        >
          {details}
        </DetailsPopover>
      </td>
      <td className={CELL}>
        <DetailsPopover
          label={row.title}
          wide={wide}
          trigger={
            <PreviewButton>
              <PreviewLine
                action={row.action}
                details={row.details}
                title={row.title}
              />
            </PreviewButton>
          }
        >
          {details}
        </DetailsPopover>
      </td>
      <td className={CELL}>
        {row.about && <PeoplePills people={[row.about]} />}
      </td>
      <td className={CELL}>
        <AutomationPill
          id={row.automationId}
          name={automations.name(row.automationId, row.automationName)}
          live={automations.live(row.automationId)}
        />
      </td>
      <td className={`${CELL} whitespace-nowrap text-sm text-gray-500`}>
        {formatDateTime(row.at)}
      </td>
    </tr>
  );
}

/**
 * Done's page of the table: what automations did, newest first. A task's
 * Open task opens it in the tasks' own window, as Tasks does. faded:
 * another page or filter is loading.
 */
export default function ActivityTabDone({
  rows,
  faded,
}: {
  rows: ActivityRow[];
  faded: boolean;
}) {
  const ref = useReference();
  const automations = useAutomationIndex();
  // The tasks Open task opened, as Tadpole has them now.
  const [opened, setOpened] = useState<TaskResponse[] | null>(null);

  /** The tasks' own window, as Tasks opens it; tasks deleted since say so. */
  const openTasks = (taskIds: string[]) =>
    TasksService.getOrgTasks({ orgId: ref.orgId })
      .then((tasks) => {
        const found = taskIds
          .map((taskId) => tasks.find((task) => String(task.id) === taskId))
          .filter((task): task is TaskResponse => !!task);
        if (found.length) setOpened(found);
        else
          Notifications.error(
            taskIds.length > 1
              ? "These tasks were deleted."
              : "This task was deleted."
          );
      })
      .catch((e) =>
        Notifications.error(errorText(e, "The task could not be opened."))
      );

  return (
    <>
      <ActivityTable faded={faded}>
        {rows.map((row) => (
          <DoneRow
            key={row.id}
            row={row}
            automations={automations}
            onOpenTasks={openTasks}
          />
        ))}
      </ActivityTable>
      {opened && (
        <TaskViewDialog
          tasks={opened.map((task) => ({
            id: task.id,
            name: task.name,
            due_date: task.due_date,
            overdue:
              task.status === TaskStatus.PENDING &&
              isPast(new Date(task.due_date)),
          }))}
          orgId={ref.orgId}
          loadedTasks={opened}
          onClose={() => setOpened(null)}
          onChanged={() => undefined}
        />
      )}
    </>
  );
}
